import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { format } from 'date-fns/format';
import { html, nothing, type TemplateResult } from 'lit';
import { state } from 'lit/decorators.js';

import { MS_PER_DAY } from '../../core/time-axis-math.js';
import { dragTooltipStyle, dragTooltipWarnStyle } from './drag-tooltip-css.js';

/**
 * Follows the cursor during a drag session and shows the proposed range
 * plus duration. The tooltip is a plain element (not a portal) mounted
 * next to the scroll container; `position: fixed` keeps it aligned with
 * the viewport regardless of scroll state.
 */
export class GanttDragTooltip extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  @state()
  accessor visible = false;

  @state()
  accessor clientX = 0;

  @state()
  accessor clientY = 0;

  @state()
  accessor startMs = 0;

  @state()
  accessor endMs = 0;

  @state()
  accessor warning: string | undefined = undefined;

  show(opts: {
    clientX: number;
    clientY: number;
    startMs: number;
    endMs: number;
    warning?: string | undefined;
  }): void {
    this.clientX = opts.clientX;
    this.clientY = opts.clientY;
    this.startMs = opts.startMs;
    this.endMs = opts.endMs;
    this.warning = opts.warning;
    this.visible = true;
  }

  hide(): void {
    this.visible = false;
    this.warning = undefined;
  }

  override render(): TemplateResult {
    if (!this.visible) return html``;
    const durationDays = Math.max(
      0,
      Math.round((this.endMs - this.startMs) / MS_PER_DAY)
    );
    // Offset tooltip *above-right* of the cursor: a tight +8px horizontal
    // offset keeps it close without overlapping the cursor caret, and
    // rendering above (translateY -100% minus a gap) ensures the tooltip
    // never covers the bar the user is dragging. `position: fixed` on
    // the tooltip style means the `translate` resolves against the
    // viewport, not a flex parent.
    return html`
      <div
        class=${dragTooltipStyle}
        style="left:${this.clientX + 8}px; top:${this.clientY -
        8}px; transform: translateY(-100%)"
      >
        <div>${safeFormat(this.startMs)} — ${safeFormat(this.endMs)}</div>
        <div>${formatDurationDays(durationDays)}</div>
        ${this.warning
          ? html`<div class=${dragTooltipWarnStyle}>
              ${resolveWarning(this.warning)}
            </div>`
          : nothing}
      </div>
    `;
  }
}

function safeFormat(ms: number): string {
  if (!Number.isFinite(ms)) return '';
  return format(ms, 'PP');
}

function formatDurationDays(days: number): string {
  const isGerman =
    typeof navigator !== 'undefined' &&
    navigator.language.toLowerCase().startsWith('de');
  if (isGerman) return days === 1 ? '1 Tag' : `${days} Tage`;
  return days === 1 ? '1 day' : `${days} days`;
}

/**
 * Core data-view has no i18n plumbing — upstream apps (frontend/core)
 * resolve keys against their i18n registry. We ship a German/English
 * fallback here so the tooltip is readable even when the key never
 * reaches a resolver (e.g. Storybook, unit tests).
 */
function resolveWarning(key: string): string {
  const isGerman =
    typeof navigator !== 'undefined' &&
    navigator.language.toLowerCase().startsWith('de');
  const map: Record<string, [de: string, en: string]> = {
    'com.streamlain.gantt.drag.warn.start-after-end': [
      'Start muss vor dem Ende liegen',
      'Start must be before end',
    ],
    'com.streamlain.gantt.drag.warn.clamped-start': [
      'Start an Ende angepasst',
      'Start clamped to end',
    ],
    'com.streamlain.gantt.drag.warn.clamped-end': [
      'Ende an Start angepasst',
      'End clamped to start',
    ],
    'com.streamlain.gantt.dep.cycle-error': [
      'Abhängigkeit würde einen Kreis erzeugen',
      'Dependency would create a cycle',
    ],
    'com.streamlain.gantt.dep.incomplete-projection': [
      'Abhängigkeiten werden noch geladen',
      'Dependencies are still loading',
    ],
  };
  const entry = map[key];
  if (!entry) return key;
  return isGerman ? entry[0] : entry[1];
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-gantt-drag-tooltip': GanttDragTooltip;
  }
}
